import { CollectedAnswer } from "../types/training";

const PLATFORM_API_BASE_URL = "https://mokymuplatformabe-production.up.railway.app/api";

interface ApiErrorResponse {
  message?: string;
}

interface ProductApiResponse {
  user?: {
    products?: string[];
  };
}

export interface AdminProductOption {
  key: string;
  title: string;
}

export interface AdminLimits {
  bulkEmailInputCharLimit: number;
  bulkEmailLimit: number;
  userCheckConcurrency: number;
}

export interface AdminManagedUser {
  normalizedEmail: string;
  auth0Exists: boolean;
  firestoreExists: boolean;
  auth0UserId: string | null;
  emailVerified: boolean;
  name: string;
  products: string[];
  productTitles: string[];
  productCertificationStatuses: Record<string, boolean>;
}

export interface AdminBulkUpsertResult extends Partial<AdminManagedUser> {
  normalizedEmail: string;
  action: "created" | "products-added" | "unchanged" | "failed";
  isNewUser?: boolean;
  addedProducts?: string[];
  addedProductTitles?: string[];
  notificationSent?: boolean;
  error?: string;
}

export interface AdminBulkUpsertResponse {
  results: AdminBulkUpsertResult[];
  summary: {
    total: number;
    created: number;
    updated: number;
    unchanged: number;
    failed: number;
  };
}

interface AdminProductsResponse {
  products: AdminProductOption[];
  limits?: AdminLimits;
}

interface AdminUserLookupResponse {
  user: AdminManagedUser;
}

interface AdminUserCheckResponse {
  results: AdminManagedUser[];
}

interface AdminUserProductsUpdateResponse {
  user: AdminManagedUser;
  addedProducts: string[];
  removedProducts: string[];
  addedProductTitles: string[];
  removedProductTitles: string[];
}

export interface AdminManualCertificatePayload {
  selectedProduct: string;
  name: string;
  surname: string;
  birthDate: string;
  email: string;
  fieldCode: string;
  productCode: string;
  hours: string;
  date: string;
  company?: string;
}

export interface AdminManualCertificateResponse {
  message: string;
  countNumber: string;
  emailSent: boolean;
  expectedNextCount?: string;
  record: {
    name: string;
    surname: string;
    birthDate: string;
    fieldCode: string;
    countNumber: string;
    hours: string;
    productCode: string;
    company: string;
    email: string;
    date?: string;
  };
}

export interface CertificateUserRecord {
  user?: {
    name?: string;
    surname?: string;
    birthDate?: string;
    fieldCode?: string;
    productCode?: string;
    countNumber?: string;
    hours?: string;
  };
  error?: string;
}

interface CountApiResponse {
  count?: string;
}

const parseApiError = async (response: Response): Promise<string> => {
  try {
    const errorData = (await response.json()) as ApiErrorResponse;
    return errorData.message || response.statusText || "Nepavyko įvykdyti užklausos";
  } catch {
    return response.statusText || "Nepavyko įvykdyti užklausos";
  }
};

const fetchJson = async <T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> => {
  const response = await fetch(input, init);

  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  return (await response.json()) as T;
};

export const fetchUserProducts = async (user: unknown): Promise<string[]> => {
  const data = await fetchJson<ProductApiResponse>(`${PLATFORM_API_BASE_URL}/get-products`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user }),
  });

  return data.user?.products || [];
};

export const sendTrainingEmail = async (payload: {
  user: unknown;
  answers: CollectedAnswer[];
  selectedProduct: string;
}) => {
  const response = await fetch(`${PLATFORM_API_BASE_URL}/send-email`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error("Failed to send training email");
  }

  return response;
};

export const sendCertificateEmail = async (formData: FormData) => {
  const response = await fetch(`${PLATFORM_API_BASE_URL}/send-certificate`, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Nepavyko išsiųsti el. laiško");
  }

  return response;
};

export const fetchNextCountNumber = async (): Promise<string | null> => {
  const data = await fetchJson<CountApiResponse>(`${PLATFORM_API_BASE_URL}/get-count`);

  if (!data.count) {
    return null;
  }

  const match = data.count.match(/^([A-Za-z]+)(\d+)$/);

  if (!match) {
    return data.count;
  }

  const prefix = match[1];
  const number = parseInt(match[2], 10) + 1;
  const padded = number.toString().padStart(match[2].length, "0");

  return `${prefix}${padded}`;
};

export const fetchCertificateUserData = async (
  productName: string,
  userEmail?: string
): Promise<CertificateUserRecord | null> => {
  if (!productName || !userEmail) {
    return null;
  }

  return fetchJson<CertificateUserRecord>(`${PLATFORM_API_BASE_URL}/get-user-data`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      productName,
      userEmail,
    }),
  });
};

export const checkCertificateUserExists = async (
  productName: string,
  userEmail?: string
): Promise<boolean> => {
  if (!productName || !userEmail) {
    return false;
  }

  const response = await fetch(`${PLATFORM_API_BASE_URL}/get-user-data`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      productName,
      userEmail,
    }),
  });

  if (response.status === 404) {
    return false;
  }

  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  return true;
};

export const fetchAdminProducts = async (): Promise<AdminProductsResponse> => {
  return fetchJson<AdminProductsResponse>(`${PLATFORM_API_BASE_URL}/admin/products`);
};

export const checkAdminUsers = async (rawEmails: string): Promise<AdminManagedUser[]> => {
  const data = await fetchJson<AdminUserCheckResponse>(`${PLATFORM_API_BASE_URL}/admin/users/check`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ rawEmails }),
  });

  return data.results;
};

export const upsertAdminUsers = async (
  rawEmails: string,
  products: string[]
): Promise<AdminBulkUpsertResponse> => {
  return fetchJson<AdminBulkUpsertResponse>(`${PLATFORM_API_BASE_URL}/admin/users/bulk-upsert`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ rawEmails, products }),
  });
};

export const lookupAdminUser = async (email: string): Promise<AdminManagedUser> => {
  const data = await fetchJson<AdminUserLookupResponse>(`${PLATFORM_API_BASE_URL}/admin/users/lookup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

  return data.user;
};

export const updateAdminUserProducts = async (
  email: string,
  products: string[]
): Promise<AdminUserProductsUpdateResponse> => {
  return fetchJson<AdminUserProductsUpdateResponse>(
    `${PLATFORM_API_BASE_URL}/admin/users/products`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, products }),
    }
  );
};

export const createAdminManualCertificate = async (
  payload: FormData
): Promise<AdminManualCertificateResponse> => {
  return fetchJson<AdminManualCertificateResponse>(
    `${PLATFORM_API_BASE_URL}/admin/certificates/manual-completion`,
    {
      method: "POST",
      body: payload,
    }
  );
};
